import { z } from 'zod';
import { parseResultPath, parseRoomPath, resultPath, roomPath } from '../../shared/routes';
import { MAX_ROUND_ID_LENGTH } from '../../shared/config';
import {
  explicitArchiveNavigation,
  hasRoomContext,
  initialNavigation,
  type Navigation,
  visibleNavigation,
} from './navigation';

const pageSchema = z.enum(['menu', 'solo', 'versus', 'host', 'join', 'leaderboard', 'competitive']);
const navigationSegmentSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('page'), page: pageSchema }),
  z.object({
    kind: z.literal('room'),
    roomCode: z.string().optional(),
    lobbyRoundId: z.string().optional(),
  }),
  z.object({ kind: z.literal('settings') }),
  z.object({ kind: z.literal('archive'), roundId: z.string() }),
]);
const storedNavigationSchema = z.object({ segments: z.array(navigationSegmentSchema).min(1) });
const historyEntrySchema = z.object({
  spellieNavigation: z.literal(1),
  session: z.string(),
  position: z.number().int().nonnegative(),
  navigation: storedNavigationSchema,
});
type StoredNavigation = z.infer<typeof storedNavigationSchema>;
type HistoryEntry = z.infer<typeof historyEntrySchema>;

type PopHandler = (
  navigation: Navigation,
  isCurrent: () => boolean,
  commit: (navigation: Navigation, path: string) => void,
) => Promise<boolean>;

export interface InitialRoute {
  roomCode: string | null;
  resultId: string | null;
}

export interface NavigationUrlState {
  roomCode: string;
  archivedRoundId: string | null;
}

/** Leave the app entirely and load it fresh at `/`, dropping every in-memory state. */
export function reloadAtRoot(): void {
  window.location.replace('/');
}

/** What the address bar asked for when the app mounted. */
export function readInitialRoute(pathname = window.location.pathname): InitialRoute {
  const resultId = parseResultPath(pathname);
  return {
    roomCode: parseRoomPath(pathname),
    resultId: resultId && resultId.length <= MAX_ROUND_ID_LENGTH ? resultId : null,
  };
}

/**
 * Reloads retain menu/modal history, while room and result URLs remain the deep-link authority.
 * A room URL keeps the path that entered it when its own entry names the same room; only a
 * link without that entry falls back to the Join form.
 */
export function readInitialNavigation(route: InitialRoute): Navigation {
  const routeNavigation = initialNavigation(route);
  if (route.resultId) return routeNavigation;
  const stored = parseHistoryEntry(window.history.state);
  if (!stored) return routeNavigation;
  const restored = restoreNavigation(stored.navigation);
  if (route.roomCode) return reloadedRoom(restored, route.roomCode) ?? routeNavigation;
  return hasRoomContext(restored) || explicitArchiveNavigation(restored) ? routeNavigation : restored;
}

/** The room entry reopened at its entering parent; the server, not history, restores its phase. */
function reloadedRoom(stored: Navigation, roomCode: string): Navigation | null {
  const visible = visibleNavigation(stored);
  if (visible.kind !== 'room' || visible.roomCode !== roomCode || hasRoomContext(visible.parent)) return null;
  return { kind: 'room', parent: visible.parent, roomCode };
}

/** Project the current navigation and authoritative session result into its canonical URL. */
export function navigationPath(navigation: Navigation, session: NavigationUrlState): string {
  const archive = explicitArchiveNavigation(navigation);
  if (archive) return resultPath(archive.roundId);
  const visible = visibleNavigation(navigation);
  if (visible.kind !== 'room') return '/';
  if (session.archivedRoundId) return resultPath(session.archivedRoundId);
  return session.roomCode ? roomPath(session.roomCode) : '/';
}

/**
 * Owns one app lifetime's History API resource. Pop requests remain provisional until
 * the app accepts them, so a cancelled leave reverses to the last committed URL/entry.
 */
export class BrowserNavigationHistory {
  private readonly session = instanceId();
  private readonly floor: number;
  private position: number;
  private committedPosition: number;
  private currentNavigation: Navigation;
  private restoring: Pick<HistoryEntry, 'session' | 'position'> | null = null;
  private semanticBack: Navigation | null = null;
  private deferredReplace: { navigation: Navigation; path: string } | null = null;
  private provisionalRevision: number | null = null;
  private revision = 0;
  private disposed = false;

  /** `initialPath` defaults to the mounted URL, kept as-is so an invitation stays reloadable. */
  constructor(
    initialNavigation: Navigation,
    initialPath: string = window.location.pathname,
    private readonly onPop: PopHandler,
  ) {
    const previous = parseHistoryEntry(window.history.state);
    this.position = previous?.position ?? 0;
    this.committedPosition = this.position;
    this.floor = this.position;
    this.currentNavigation = initialNavigation;
    this.replace(initialNavigation, initialPath);
    window.addEventListener('popstate', this.handlePop);
  }

  push(navigation: Navigation, path: string): void {
    this.position = this.committedPosition + 1;
    this.committedPosition = this.position;
    this.currentNavigation = navigation;
    window.history.pushState(this.entry(navigation), '', path);
  }

  replace(navigation: Navigation, path: string): void {
    this.currentNavigation = navigation;
    if (this.provisionalRevision !== null) {
      // Server state can finish joining or archiving while a leave confirmation is open.
      // Keep that update for the committed entry instead of overwriting the traversed target.
      this.deferredReplace = { navigation, path };
      return;
    }
    this.writeCurrent(navigation, path);
  }

  /** Use native traversal only for entries created after this app instance mounted. */
  backTo(navigation: Navigation): boolean {
    if (this.committedPosition <= this.floor) return false;
    this.semanticBack = navigation;
    window.history.back();
    return true;
  }

  dispose(): void {
    this.disposed = true;
    this.revision++;
    window.removeEventListener('popstate', this.handlePop);
  }

  private readonly handlePop = (event: PopStateEvent): void => {
    const target = parseHistoryEntry(event.state);
    if (!target) {
      // A same-document entry not owned by Spellie has no safe app-state projection.
      window.location.reload();
      return;
    }
    this.position = target.position;
    if (this.restoring?.session === target.session && this.restoring.position === target.position) {
      this.restoring = null;
      this.provisionalRevision = null;
      if (this.deferredReplace) {
        const deferred = this.deferredReplace;
        this.deferredReplace = null;
        this.writeCurrent(deferred.navigation, deferred.path);
      }
      return;
    }

    const requested = this.semanticBack ?? restoreNavigation(target.navigation);
    this.semanticBack = null;
    const revision = ++this.revision;
    this.provisionalRevision = revision;
    const commit = (navigation: Navigation, path: string): void => {
      if (this.disposed || revision !== this.revision) return;
      this.provisionalRevision = null;
      this.deferredReplace = null;
      this.currentNavigation = navigation;
      this.writeCurrent(navigation, path);
    };
    void this.onPop(requested, () => !this.disposed && revision === this.revision, commit).then(
      (accepted) => {
        if (this.disposed || revision !== this.revision || accepted) return;
        const distance = this.committedPosition - target.position;
        if (distance === 0) {
          this.provisionalRevision = null;
          const deferred = this.deferredReplace;
          this.deferredReplace = null;
          this.writeCurrent(
            deferred?.navigation ?? this.currentNavigation,
            deferred?.path ?? window.location.pathname,
          );
          return;
        }
        this.restoring = { session: this.session, position: this.committedPosition };
        window.history.go(distance);
      },
    );
  };

  private writeCurrent(navigation: Navigation, path: string): void {
    this.committedPosition = this.position;
    window.history.replaceState(this.entry(navigation), '', path);
  }

  private entry(navigation: Navigation): HistoryEntry {
    return {
      spellieNavigation: 1,
      session: this.session,
      position: this.position,
      navigation: storeNavigation(navigation),
    };
  }
}

function storeNavigation(navigation: Navigation): StoredNavigation {
  const segments: StoredNavigation['segments'] = [];
  let current: Navigation | null = navigation;
  while (current) {
    if (current.kind === 'page') segments.unshift({ kind: 'page', page: current.page });
    else if (current.kind === 'room')
      segments.unshift({
        kind: 'room',
        ...(current.roomCode ? { roomCode: current.roomCode } : {}),
        ...(current.lobbyRoundId ? { lobbyRoundId: current.lobbyRoundId } : {}),
      });
    else if (current.kind === 'settings') segments.unshift({ kind: 'settings' });
    else segments.unshift({ kind: 'archive', roundId: current.roundId });
    current = current.parent;
  }
  return { segments };
}

function restoreNavigation(stored: StoredNavigation): Navigation {
  let navigation: Navigation | null = null;
  for (const segment of stored.segments) {
    if (segment.kind === 'page') {
      navigation = { kind: 'page', page: segment.page, parent: navigation };
      continue;
    }
    if (!navigation) return initialNavigation({ roomCode: null, resultId: null });
    if (segment.kind === 'room')
      navigation = {
        kind: 'room',
        parent: navigation,
        ...(segment.roomCode ? { roomCode: segment.roomCode } : {}),
        ...(segment.lobbyRoundId ? { lobbyRoundId: segment.lobbyRoundId } : {}),
      };
    else if (segment.kind === 'settings') navigation = { kind: 'settings', parent: navigation };
    else navigation = { kind: 'archive', roundId: segment.roundId, parent: navigation };
  }
  return navigation ?? initialNavigation({ roomCode: null, resultId: null });
}

/**
 * Distinguishes entries written by this app instance from stale ones after a reload.
 * `crypto.randomUUID` is unavailable on the LAN dev server (HTTP is not a secure context).
 */
function instanceId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function parseHistoryEntry(value: unknown): HistoryEntry | null {
  const parsed = historyEntrySchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
